import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { twMerge } from 'tailwind-merge';
import rehypeRevealTicks from 'flow/utils/rehype-reveal-ticks';

interface Props {
    markdown: string;
    /** Reveal the text a chunk of this many characters at a time, in CSS. */
    revealCharsPerTick?: number;
}

// A summary is a short block of body text inside a card, so headings stay close
// to the surrounding size instead of using the page-level scale, and blocks are
// separated by a single small gap.
//
// The single line breaks a customer summary prompt (Bring Your Own Prompt)
// emits are kept by `remark-breaks` below, which turns each one into a `<br>`
// at parse time. A whitespace rule here would only reach the paragraphs: a
// break inside a bullet or a quote would still fold into a space, and one on a
// list would turn the newlines the parser leaves between items into blank
// lines.
export const summaryMarkdownComponents: Components = {
    h1: ({ children }) => <h2 className="font-semibold">{children}</h2>,
    h2: ({ children }) => <h3 className="font-semibold">{children}</h3>,
    h3: ({ children }) => <h4 className="font-semibold">{children}</h4>,
    h4: ({ children }) => <h5 className="font-semibold">{children}</h5>,
    h5: ({ children }) => <h6 className="font-semibold">{children}</h6>,
    h6: ({ children }) => <h6 className="font-semibold">{children}</h6>,
    ul: ({ children }) => <ul className="list-disc ps-5">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal ps-5">{children}</ol>,
    blockquote: ({ children }) => (
        <blockquote className="border-s-2 border-gray-300 ps-2 text-gray-600 italic">
            {children}
        </blockquote>
    ),
    code: ({ children }) => (
        <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs">
            {children}
        </code>
    ),
    pre: ({ children }) => (
        // The fenced block carries the background; the `code` inside it drops
        // the inline-code styling so the two don't stack.
        <pre className="overflow-x-auto rounded bg-gray-200 p-2 text-xs [&>code]:bg-transparent [&>code]:p-0">
            {children}
        </pre>
    ),
    hr: () => <hr className="border-t border-gray-300" />,
    table: ({ children }) => (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse">{children}</table>
        </div>
    ),
    th: ({ children }) => (
        <th className="border border-gray-300 px-2 py-1 text-start font-semibold">
            {children}
        </th>
    ),
    td: ({ children }) => (
        <td className="border border-gray-300 px-2 py-1 text-start">
            {children}
        </td>
    ),
    // A summary is model output, so every part of it is untrusted. Formatting is
    // all it is allowed to carry: a link keeps its label and loses its
    // destination, so nothing on the card can be clicked into navigating
    // somewhere the model chose. The same for an image, which would not even
    // wait to be clicked — it would fetch from wherever the model named the
    // moment the card rendered — so only its description is kept.
    a: ({ children }) => <>{children}</>,
    img: ({ alt }) => <>{alt}</>,
};

/**
 * Renders an AI call summary as markdown. Summaries come back from a model —
 * the standard prompt emits plain prose, a customer's own prompt commonly emits
 * headings, bullets and emphasis — so the text is untrusted, and the constructs
 * that reach outside the card, links and images, are rendered as the text they
 * carry. See summaryMarkdownComponents above.
 *
 * Anything shaped like a tag is shown as the characters it is, rather than
 * dropped. `skipHtml` would delete it instead, which loses content without
 * buying anything: the raw node becomes a text node either way — react-markdown
 * only ever splices it out or converts it — so `Kunden <Anna> ringde` would
 * silently lose the name. It is `rehype-raw` below that would turn raw HTML
 * into elements, and it is deliberately absent; `skipHtml` would not help if it
 * were there, since raw nodes become elements before that option is consulted.
 */
const TranscriptionSummaryMarkdown = memo(
    ({ markdown, revealCharsPerTick }: Props) => {
        // Rebuilt only when the pace changes, so the parse below keeps the same
        // plugin list and react-markdown has no reason to parse again.
        const rehypePlugins = useMemo(
            () =>
                revealCharsPerTick
                    ? [
                          [
                              rehypeRevealTicks,
                              { charsPerTick: revealCharsPerTick },
                          ],
                      ]
                    : [],
            [revealCharsPerTick],
        );

        return (
            <div
                className={twMerge(
                    'flex flex-col gap-2',
                    revealCharsPerTick && 'summary-reveal',
                )}
            >
                <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    // Never add `rehype-raw` here: it is what would turn the
                    // raw HTML in a summary into real elements.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    rehypePlugins={rehypePlugins as any}
                    components={summaryMarkdownComponents}
                >
                    {markdown}
                </ReactMarkdown>
            </div>
        );
    },
);

TranscriptionSummaryMarkdown.displayName = 'TranscriptionSummaryMarkdown';

export default TranscriptionSummaryMarkdown;
